import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import UnifiedCommentDto from '../../types/comment.types';
import {
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface ThreadCommentProps {
  comment: UnifiedCommentDto;
  indentLevel: number; // 0 = top-level, 1 = first reply, 2 = nested reply, etc.
  isReplying: boolean;
  replyText: string;
  showReplyInput: boolean;
  children?: React.ReactNode; // For nested replies
  onReplyTextChange: (text: string) => void;
  onReplySubmit: () => void;
  onReplyCancel: () => void;
  onShowReplyInput: () => void;
  onLoadMoreReplies?: () => void;
  hasMoreReplies?: boolean;
  isLoadingReplies?: boolean;
  hasLoadedReplies?: boolean;
  onHideComment?: () => void;
  onDeleteComment?: () => void;
  isHidingComment?: boolean;
  hasOptimisticUpdate?: boolean;
  isDeletingComment?: boolean;
}

const ThreadComment: React.FC<ThreadCommentProps> = ({
  comment,
  indentLevel,
  isReplying,
  replyText,
  showReplyInput,
  children,
  onReplyTextChange,
  onReplySubmit,
  onReplyCancel,
  onShowReplyInput,
  onLoadMoreReplies,
  hasMoreReplies,
  isLoadingReplies,
  hasLoadedReplies,
  onHideComment,
  onDeleteComment,
  isHidingComment = false,
  hasOptimisticUpdate = false,
  isDeletingComment = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isTextLong = (text: string) => {
    return text.length > 150;
  };

  // Calculate indentation - make it more pronounced for clarity
  const getIndentStyles = (level: number) => {
    // Use a more noticeable indentation (32px per level instead of 8px)
    const baseMargin = 32;
    const marginLeft = level * baseMargin;

    return {
      marginLeft: `${marginLeft}px`,
    };
  };

  const styles = getIndentStyles(indentLevel);

  // Get reply count for display
  const replyCount = comment.replyCount || 0;

  // Determine if we should show the "load replies" button
  const shouldShowLoadRepliesButton = replyCount > 0 && !hasLoadedReplies;

  return (
    <div className="space-y-2">
      {/* Main Comment */}
      <div
        className="bg-greythree rounded-lg p-3 relative"
        style={{ marginLeft: styles.marginLeft }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex items-start space-x-3">
          {/* Avatar - keeping same size for all comments */}
          <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-gray-200">
              {comment.username.charAt(0).toUpperCase()}
            </span>
          </div>

          {/* Comment Content */}
          {/* Comment Content */}
          <div className="flex-1">
            {/* Header with username and timestamp */}
            <div className="flex items-center justify-between h-6">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-white text-sm">
                  {comment.username}
                </span>
                <span className="text-gray-400 text-sm">
                  {formatDistanceToNow(new Date(comment.platformCreatedAt), {
                    addSuffix: true,
                  }).replace('about ', '')}
                </span>

                {/* Hidden Status Indicator */}
                <div className="flex items-center pt-1 space-x-1">
                  <span
                    className={`border rounded-xl text-xs  px-1.5 py-0 font-semibold ${
                      comment.hidden
                        ? 'text-orange bg-orange/10 border-orange/80'
                        : 'text-green-400 border-green-500/30 bg-green-500/10'
                    }`}
                  >
                    {comment.hidden ? 'Hidden' : 'Not Hidden'}
                  </span>
                  <div className="relative group flex items-center">
                    <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-pointer" />
                    {comment.moderationReason && (
                      <div className="absolute left-6 top-1/2 -translate-y-1/2 z-20 hidden group-hover:block bg-black bg-opacity-90 text-white text-xs rounded px-4 py-2 shadow-lg whitespace-pre-line max-w-md min-w-[200px]">
                        {comment.moderationReason.split('|')[0]}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Hide and Delete buttons - moved to the right */}
              {isHovered && (
                <div className="flex space-x-1">
                  <button
                    className={`text-xs flex items-center bg-white bg-opacity-10 hover:bg-white hover:bg-opacity-20 rounded-lg py-1 px-1 ${
                      hasOptimisticUpdate ? 'opacity-75' : ''
                    }`}
                    onClick={onHideComment}
                    disabled={isHidingComment}
                    title={
                      hasOptimisticUpdate
                        ? 'Updating...'
                        : comment.hidden
                        ? 'Show comment'
                        : 'Hide comment'
                    }
                  >
                    {isHidingComment ? (
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : comment.hidden ? (
                      <EyeSlashIcon
                        className={`text-white w-4 h-4 cursor-pointer ${
                          hasOptimisticUpdate ? 'animate-pulse' : ''
                        }`}
                      />
                    ) : (
                      <EyeIcon
                        className={`text-white w-4 h-4 cursor-pointer ${
                          hasOptimisticUpdate ? 'animate-pulse' : ''
                        }`}
                      />
                    )}
                  </button>
                  <button
                    onClick={onDeleteComment}
                    disabled={isDeletingComment || isHidingComment}
                    className={`text-red text-xs bg-red bg-opacity-10 flex hover:bg-red hover:bg-opacity-20 rounded-lg py-1 px-1 items-center disabled:opacity-50 disabled:cursor-not-allowed ${
                      isDeletingComment ? 'animate-pulse' : ''
                    }`}
                    title={isDeletingComment ? 'Deleting...' : 'Delete comment'}
                  >
                    {isDeletingComment ? (
                      <div className="w-4 h-4 animate-spin rounded-full border-2 border-red border-t-transparent" />
                    ) : (
                      <TrashIcon className="w-4 h-4 cursor-pointer" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Comment Message with truncation */}
            <div className="mt-1">
              <p
                className={`text-gray-200 text-sm ${
                  !isExpanded && isTextLong(comment.message)
                    ? 'line-clamp-3'
                    : ''
                }`}
              >
                {comment.message}
              </p>

              {/* Show More/Less button */}
              {isTextLong(comment.message) && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium mt-1"
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
            </div>

            {/* Attachment if present */}
            {comment.attachmentUrl && (
              <div className="mt-2">
                <img
                  src={comment.attachmentUrl}
                  alt="Comment attachment"
                  className="max-w-xs rounded-lg"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Reply Button - positioned below the comment */}
        <div className="flex items-center space-x-4 mt-2 ml-9">
          <button
            onClick={onShowReplyInput}
            className="text-gray-400 hover:text-blue-400 text-sm font-medium"
          >
            Reply
          </button>

          {/* Show replies button - only if there are replies and we haven't loaded them yet */}
          {shouldShowLoadRepliesButton && (
            <button
              onClick={onLoadMoreReplies}
              disabled={isLoadingReplies}
              className="text-blue-400 hover:text-blue-300 text-sm font-medium disabled:opacity-50"
            >
              {isLoadingReplies ? (
                'Loading...'
              ) : (
                <span>
                  {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Reply Input */}
      {showReplyInput && (
        <div
          className="bg-greythree rounded-lg p-3"
          style={{ marginLeft: `${parseInt(styles.marginLeft) + 32}px` }}
        >
          <div className="flex space-x-2">
            <textarea
              value={replyText}
              onChange={(e) => onReplyTextChange(e.target.value)}
              placeholder={`Reply to ${comment.username}...`}
              className="flex-1 bg-greythree text-white rounded p-2 text-sm resize-none border border-gray-600 focus:border-violet focus:outline-none"
              rows={2}
            />
            <div className="flex flex-col space-y-1">
              <button
                onClick={onReplySubmit}
                disabled={!replyText.trim() || isReplying}
                className={`px-3 py-1 bg-yellow hover:bg-opacity-80 disabled:bg-gray-600 disabled:opacity-50 text-black rounded text-sm ${
                  !replyText.trim() ? 'text-white' : 'text-black'
                }`}
              >
                {isReplying ? 'Sending...' : 'Reply'}
              </button>
              <button
                onClick={onReplyCancel}
                className="px-3 py-1 bg-gray-600 hover:bg-gray-600 bg-opacity-60 text-white rounded text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Child Replies */}
      {children && <div className="space-y-2">{children}</div>}

      {/* Load More Replies Button (for when we have loaded some but there are more) */}
      {hasMoreReplies && hasLoadedReplies && (
        <div
          className="text-center"
          style={{ marginLeft: `${parseInt(styles.marginLeft) + 32}px` }}
        >
          <button
            onClick={onLoadMoreReplies}
            disabled={isLoadingReplies}
            className="text-blue-400 hover:text-blue-300 text-sm font-medium disabled:opacity-50"
          >
            {isLoadingReplies ? 'Loading...' : 'Load more replies'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ThreadComment;
